"use client";

import { useState, useMemo } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

type TenderType = "CASH" | "CARD" | "GIFT_CARD" | "MOBILE_PAY" | "SPLIT";

interface SplitEntry {
  method: Exclude<TenderType, "SPLIT">;
  amount: number;
}

interface PaymentModalProps {
  orderId: string;
  orderNumber: string;
  total: number;
  onClose: () => void;
  onPaymentComplete: () => void;
}

const TENDER_TYPES: Array<{ value: TenderType; label: string; icon: string }> = [
  {
    value: "CASH",
    label: "Cash",
    icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z",
  },
  {
    value: "CARD",
    label: "Credit / Debit",
    icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  },
  {
    value: "GIFT_CARD",
    label: "Gift Card",
    icon: "M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7",
  },
  {
    value: "MOBILE_PAY",
    label: "Mobile Pay",
    icon: "M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z",
  },
  {
    value: "SPLIT",
    label: "Split Payment",
    icon: "M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4",
  },
];

const QUICK_TIP_PERCENTAGES = [15, 18, 20, 25];

const QUICK_CASH_AMOUNTS = [500, 1000, 2000, 5000, 10000];

export function PaymentModal({
  orderId,
  orderNumber,
  total,
  onClose,
  onPaymentComplete,
}: PaymentModalProps) {
  const [step, setStep] = useState<"tender" | "details" | "processing" | "complete">("tender");
  const [selectedTender, setSelectedTender] = useState<TenderType | null>(null);
  const [tipPercent, setTipPercent] = useState<number | null>(null);
  const [customTip, setCustomTip] = useState("");
  const [cashTendered, setCashTendered] = useState("");
  const [splits, setSplits] = useState<SplitEntry[]>([
    { method: "CARD", amount: 0 },
    { method: "CASH", amount: 0 },
  ]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tipAmount = useMemo(() => {
    if (customTip) return Math.round(parseFloat(customTip) * 100) || 0;
    if (tipPercent) return Math.round(total * tipPercent / 100);
    return 0;
  }, [tipPercent, customTip, total]);

  const grandTotal = total + tipAmount;

  const cashTenderedCents = useMemo(() => {
    if (!cashTendered) return 0;
    return Math.round(parseFloat(cashTendered) * 100) || 0;
  }, [cashTendered]);

  const changeDue = useMemo(() => {
    if (selectedTender !== "CASH") return 0;
    return Math.max(0, cashTenderedCents - grandTotal);
  }, [cashTenderedCents, grandTotal, selectedTender]);

  const splitTotal = useMemo(
    () => splits.reduce((sum, s) => sum + s.amount, 0),
    [splits],
  );

  const splitRemaining = grandTotal - splitTotal;

  function handleTenderSelect(tender: TenderType) {
    setSelectedTender(tender);
    setStep("details");
  }

  function updateSplitAmount(index: number, value: string) {
    const cents = Math.round(parseFloat(value) * 100) || 0;
    setSplits((prev) => prev.map((s, i) => (i === index ? { ...s, amount: cents } : s)));
  }

  function updateSplitMethod(index: number, method: Exclude<TenderType, "SPLIT">) {
    setSplits((prev) => prev.map((s, i) => (i === index ? { ...s, method } : s)));
  }

  function addSplitEntry() {
    setSplits((prev) => [...prev, { method: "CARD", amount: 0 }]);
  }

  function removeSplitEntry(index: number) {
    if (splits.length <= 2) return;
    setSplits((prev) => prev.filter((_, i) => i !== index));
  }

  async function processPayment() {
    if (!selectedTender) return;
    setProcessing(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {
        method: selectedTender,
        amount: grandTotal,
        tipAmount,
      };

      if (selectedTender === "CASH") {
        body.cashTendered = cashTenderedCents;
        body.changeDue = changeDue;
      }

      if (selectedTender === "CARD") {
        body.cardLast4 = "0000";
        body.cardBrand = "demo";
      }

      if (selectedTender === "SPLIT") {
        body.splits = splits.map((s) => ({
          method: s.method,
          amount: s.amount,
        }));
      }

      const res = await fetch(`/api/orders/${orderId}/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Payment failed");
      }

      setStep("complete");
      setTimeout(() => {
        onPaymentComplete();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Payment failed");
    } finally {
      setProcessing(false);
    }
  }

  const canProcessCash = selectedTender === "CASH" && cashTenderedCents >= grandTotal;
  const canProcessSplit = selectedTender === "SPLIT" && splitRemaining <= 0;
  const canProcess =
    selectedTender === "CARD" ||
    selectedTender === "GIFT_CARD" ||
    selectedTender === "MOBILE_PAY" ||
    canProcessCash ||
    canProcessSplit;

  return (
    <Modal isOpen onClose={onClose} title={`Payment — ${orderNumber}`} size="lg">
      {step === "tender" && (
        <div className="space-y-6">
          {/* Order total */}
          <div className="text-center py-4">
            <p className="text-sm text-text-muted mb-1">Order Total</p>
            <p className="text-4xl font-bold text-white">{formatCurrency(total)}</p>
          </div>

          {/* Tip selection */}
          <div>
            <p className="text-sm font-medium text-text-muted mb-3">Add Tip</p>
            <div className="grid grid-cols-4 gap-2">
              {QUICK_TIP_PERCENTAGES.map((pct) => (
                <button
                  key={pct}
                  onClick={() => { setTipPercent(pct); setCustomTip(""); }}
                  className={cn(
                    "rounded-xl py-3 text-sm font-medium transition-all active:scale-95",
                    tipPercent === pct
                      ? "bg-accent text-white"
                      : "bg-white/5 text-text-muted hover:bg-white/10 hover:text-white",
                  )}
                >
                  {pct}%
                  <span className="block text-[10px] mt-0.5 opacity-60">
                    {formatCurrency(Math.round(total * pct / 100))}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => { setTipPercent(null); setCustomTip(""); }}
                className={cn(
                  "rounded-xl px-4 py-2 text-sm transition-all",
                  tipPercent === null && !customTip
                    ? "bg-white/10 text-white"
                    : "bg-white/5 text-text-dim hover:bg-white/10",
                )}
              >
                No Tip
              </button>
              <Input
                placeholder="Custom $"
                value={customTip}
                onChange={(e) => { setCustomTip(e.target.value); setTipPercent(null); }}
                className="flex-1"
              />
            </div>
            {tipAmount > 0 && (
              <p className="text-sm text-accent mt-2 text-center">
                Tip: {formatCurrency(tipAmount)} — Grand Total: {formatCurrency(grandTotal)}
              </p>
            )}
          </div>

          {/* Tender type selection */}
          <div>
            <p className="text-sm font-medium text-text-muted mb-3">Select Payment Method</p>
            <div className="grid grid-cols-2 gap-3">
              {TENDER_TYPES.map((tender) => (
                <button
                  key={tender.value}
                  onClick={() => handleTenderSelect(tender.value)}
                  className="flex items-center gap-3 rounded-2xl bg-white/5 px-4 py-4 text-left transition-all hover:bg-white/10 active:scale-[0.97] touch-target"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={tender.icon} />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-white">{tender.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === "details" && selectedTender === "CASH" && (
        <div className="space-y-6">
          <div className="text-center py-2">
            <p className="text-sm text-text-muted mb-1">Amount Due</p>
            <p className="text-3xl font-bold text-white">{formatCurrency(grandTotal)}</p>
          </div>

          {/* Quick cash amounts */}
          <div>
            <p className="text-sm font-medium text-text-muted mb-3">Quick Amounts</p>
            <div className="grid grid-cols-3 gap-2">
              {QUICK_CASH_AMOUNTS.filter((a) => a >= grandTotal).map((amount) => (
                <button
                  key={amount}
                  onClick={() => setCashTendered((amount / 100).toFixed(2))}
                  className={cn(
                    "rounded-xl py-3 text-sm font-medium transition-all active:scale-95",
                    cashTenderedCents === amount
                      ? "bg-success text-white"
                      : "bg-white/5 text-text-muted hover:bg-white/10",
                  )}
                >
                  {formatCurrency(amount)}
                </button>
              ))}
              <button
                onClick={() => setCashTendered((grandTotal / 100).toFixed(2))}
                className={cn(
                  "rounded-xl py-3 text-sm font-medium transition-all active:scale-95",
                  cashTenderedCents === grandTotal
                    ? "bg-success text-white"
                    : "bg-white/5 text-text-muted hover:bg-white/10",
                )}
              >
                Exact
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm text-text-muted mb-1 block">Cash Tendered</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={cashTendered}
              onChange={(e) => setCashTendered(e.target.value)}
              className="text-2xl text-center font-mono"
            />
          </div>

          {cashTenderedCents > 0 && cashTenderedCents >= grandTotal && (
            <div className="rounded-2xl bg-success/10 border border-success/20 p-4 text-center">
              <p className="text-sm text-success mb-1">Change Due</p>
              <p className="text-3xl font-bold text-success">{formatCurrency(changeDue)}</p>
            </div>
          )}

          {error && (
            <p className="text-sm text-danger text-center">{error}</p>
          )}

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep("tender")} className="flex-1">
              Back
            </Button>
            <Button
              variant="primary"
              size="xl"
              onClick={processPayment}
              disabled={!canProcessCash || processing}
              loading={processing}
              className="flex-[2] bg-success hover:bg-emerald-600"
            >
              Complete Payment
            </Button>
          </div>
        </div>
      )}

      {step === "details" && selectedTender === "CARD" && (
        <div className="space-y-6">
          <div className="text-center py-4">
            <p className="text-sm text-text-muted mb-1">Charge to Card</p>
            <p className="text-4xl font-bold text-white">{formatCurrency(grandTotal)}</p>
            {tipAmount > 0 && (
              <p className="text-sm text-accent mt-1">Includes {formatCurrency(tipAmount)} tip</p>
            )}
          </div>

          <div className="rounded-2xl bg-white/5 p-6 text-center">
            <svg className="h-16 w-16 mx-auto text-accent mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <p className="text-sm text-text-muted">
              Tap, insert, or swipe card on terminal
            </p>
            <p className="text-xs text-text-dim mt-1">
              Demo mode — payment will be simulated
            </p>
          </div>

          {error && (
            <p className="text-sm text-danger text-center">{error}</p>
          )}

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep("tender")} className="flex-1">
              Back
            </Button>
            <Button
              variant="primary"
              size="xl"
              onClick={processPayment}
              disabled={processing}
              loading={processing}
              className="flex-[2] bg-success hover:bg-emerald-600"
            >
              Process Card
            </Button>
          </div>
        </div>
      )}

      {step === "details" && (selectedTender === "GIFT_CARD" || selectedTender === "MOBILE_PAY") && (
        <div className="space-y-6">
          <div className="text-center py-4">
            <p className="text-sm text-text-muted mb-1">
              {selectedTender === "GIFT_CARD" ? "Gift Card Payment" : "Mobile Payment"}
            </p>
            <p className="text-4xl font-bold text-white">{formatCurrency(grandTotal)}</p>
          </div>

          <div className="rounded-2xl bg-white/5 p-6 text-center">
            <p className="text-sm text-text-muted">
              {selectedTender === "GIFT_CARD"
                ? "Scan or enter gift card number"
                : "Customer pays via Apple Pay / Google Pay"}
            </p>
            <p className="text-xs text-text-dim mt-1">Demo mode — payment will be simulated</p>
          </div>

          {error && <p className="text-sm text-danger text-center">{error}</p>}

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep("tender")} className="flex-1">
              Back
            </Button>
            <Button
              variant="primary"
              size="xl"
              onClick={processPayment}
              disabled={processing}
              loading={processing}
              className="flex-[2] bg-success hover:bg-emerald-600"
            >
              Complete Payment
            </Button>
          </div>
        </div>
      )}

      {step === "details" && selectedTender === "SPLIT" && (
        <div className="space-y-6">
          <div className="text-center py-2">
            <p className="text-sm text-text-muted mb-1">Split Payment</p>
            <p className="text-3xl font-bold text-white">{formatCurrency(grandTotal)}</p>
          </div>

          <div className="space-y-3">
            {splits.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <select
                  value={entry.method}
                  onChange={(e) => updateSplitMethod(index, e.target.value as Exclude<TenderType, "SPLIT">)}
                  className="rounded-xl bg-white/5 border border-surface-border px-3 py-2.5 text-sm text-white"
                >
                  <option value="CARD">Card</option>
                  <option value="CASH">Cash</option>
                  <option value="GIFT_CARD">Gift Card</option>
                  <option value="MOBILE_PAY">Mobile Pay</option>
                </select>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={entry.amount > 0 ? (entry.amount / 100).toFixed(2) : ""}
                  onChange={(e) => updateSplitAmount(index, e.target.value)}
                  className="flex-1 font-mono"
                />
                {splits.length > 2 && (
                  <button
                    onClick={() => removeSplitEntry(index)}
                    className="rounded-lg p-2 text-text-dim hover:text-danger hover:bg-danger/10 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={addSplitEntry}
            className="w-full rounded-xl border border-dashed border-surface-border-2 py-2.5 text-sm text-text-muted hover:bg-white/5 hover:text-white transition-colors"
          >
            + Add Split
          </button>

          <div className={cn(
            "rounded-xl p-3 text-center text-sm font-medium",
            splitRemaining <= 0 ? "bg-success/10 text-success" : "bg-warning/10 text-warning",
          )}>
            {splitRemaining <= 0
              ? "Fully allocated"
              : `Remaining: ${formatCurrency(splitRemaining)}`}
          </div>

          {error && <p className="text-sm text-danger text-center">{error}</p>}

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep("tender")} className="flex-1">
              Back
            </Button>
            <Button
              variant="primary"
              size="xl"
              onClick={processPayment}
              disabled={!canProcessSplit || processing}
              loading={processing}
              className="flex-[2] bg-success hover:bg-emerald-600"
            >
              Process Split
            </Button>
          </div>
        </div>
      )}

      {step === "complete" && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-success/20 mb-4">
            <svg className="h-10 w-10 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Payment Complete</h3>
          <p className="text-text-muted">
            {formatCurrency(grandTotal)} paid via {selectedTender?.replace("_", " ")}
          </p>
          {changeDue > 0 && (
            <p className="text-lg font-semibold text-success mt-2">
              Change: {formatCurrency(changeDue)}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
